import React, { useEffect, useState } from 'react';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import SearchSelect from 'components/ui/SearchSelect';
import DateTimePicker, {
  DateTimePickerValue,
} from 'components/ui/DateTimePicker';
import {
  COMMON_FIELDS,
  SEGMENT_OPERATORS,
  SEGMENT_OPERATORS_GROUPS,
  SEGMENT_SOURCE_OPERATORS,
} from 'constants/segmentConstants';
import { useSelector } from 'react-redux';
import { selectAttributes } from 'store/attributes';
import {
  selectSegments,
  selectSegmentsLoading,
} from 'store/segments/segments.selector';
import { IAttribute } from 'types/attributes/attributes.types';
import { Condition, ConditionValue } from 'types/segments/segments.types';
import { validateCondition } from 'utils/conditionValidation';

type Err = { field?: string; operator?: string; value?: string };

const OP = {
  HAS_PROPERTY: 'has_property',
  AT_LEAST_ONE: 'at_least_one',
  DEFAULT: 'default',
} as const;
const DEF = {
  has_property: { property: '', operator: 'equals', value: null },
  at_least_one: { value: null, arrayType: '' },
  default: { value: null },
} as const;
const NO_VAL_OPS = new Set([
  'between',
  'is_timestamp',
  'at_least_one',
  'has_property',
  'is_not_timestamp',
  'is_before',
  'is_after',
  'is_between',
  'exists',
  'is_valid',
  'is_invalid',
  'not_exists',
  'is_empty',
  'is_not_empty',
]);
const DATE_ONLY_OPS = new Set([
  'is_timestamp',
  'is_not_timestamp',
  'exists',
  'not_exists',
  'is_empty',
  'is_not_empty',
  'is_valid',
  'is_invalid',
  'at_least_one',
  'has_property',
]);
const TIME_FIELDS = new Set([
  'createdAt',
  'updatedAt',
  'time',
  'datetime',
  'timestamp',
]);
const INPUT_BASE = 'border border-gray-300 rounded-md';
const INPUT_NORMAL = 'min-w-[130px] px-3 py-2';
const INPUT_COMPACT = 'min-w-[100px] px-2.5 py-1.5 text-xs';
const ERR_CLS =
  'border-destructive focus:border-destructive focus:ring-destructive';
const BTN_BASE =
  'border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-left text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary';
const BTN_NORMAL = 'min-w-[130px] px-2.5 py-1.5 text-sm';
const BTN_COMPACT = 'min-w-[100px] px-2 py-1 text-xs';
const TOOLTIP =
  'absolute top-full left-1/2 -translate-x-1/2 mt-1.5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none whitespace-nowrap';
const TOOLTIP_BOX =
  'min-w-[100px] p-2 bg-foreground text-primary-foreground text-xs rounded-md shadow-xl text-center';
const FIELD_COL_NORMAL =
  'flex-shrink-0 text-sm min-w-[130px] max-w-[180px] flex flex-col';
const FIELD_COL_COMPACT =
  'flex-shrink-0 text-xs min-w-[100px] max-w-[140px] flex flex-col';
const LABEL_NORMAL = 'text-muted-foreground font-medium mb-1';
const LABEL_COMPACT = 'text-muted-foreground font-medium mb-0.5 text-xs';
const ROW_GAP_NORMAL = 'gap-2';
const ROW_GAP_COMPACT = 'gap-1.5';
const ROOT_GAP_NORMAL = 'gap-3';
const ROOT_GAP_COMPACT = 'gap-2';
const DATE_ROW_NORMAL = 'min-w-[260px]';
const DATE_ROW_COMPACT = 'min-w-[200px]';
const DATE_BTN_NORMAL = 'min-w-[120px]';
const DATE_BTN_COMPACT = 'min-w-[90px]';

function parseDt(v: ConditionValue): Date | null {
  const x = v?.datetime;
  if (x instanceof Date) return x;
  if (typeof x === 'string') {
    const d = new Date(x);
    if (!isNaN(d.getTime())) return d;
  }
  if (v?.date) {
    const d = new Date(v.date);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

function fmtDtStr(s: string): string {
  const d = new Date(s);
  return s.includes('T')
    ? d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
}

function toDtPickerVal(
  v: ConditionValue,
  which: 'start' | 'end'
): DateTimePickerValue | null {
  const raw =
    which === 'start'
      ? (v as ConditionValue)?.start
      : (v as ConditionValue)?.end;
  if (!raw) return null;
  const d = new Date(raw);
  const ts =
    which === 'start'
      ? (v as ConditionValue).startTimestamp
      : (v as ConditionValue).endTimestamp;
  return {
    datetime: d,
    timestamp: ts ?? Math.floor(d.getTime() / 1000),
    timezone: (v as ConditionValue)?.timezone || 'UTC',
  };
}

export type DataSourceOption = { value: string; label: string };
export type DataSourceOptions =
  | DataSourceOption[]
  | Record<string, DataSourceOption[]>;

export type FieldOption = { label: string; value: string; tag?: string };

export const DEFAULT_EVENT_OPERATORS: { value: string; label: string }[] = [
  { value: 'has_performed', label: 'Has performed' },
  { value: 'has_not_performed', label: 'Has not performed' },
];

export interface ConditionsFieldProps {
  condition: Condition;
  setCondition: (c: Condition) => void;
  dataSourceOptions: DataSourceOptions;
  attributeFieldOptions?: Record<string, FieldOption[]> | FieldOption[];
  segmentOptions?: FieldOption[];
  eventOptions?: FieldOption[];
  eventOperators?: { value: string; label: string }[];
  /** Use tighter layout for panels with limited height/width (e.g. workflow nodes, max-h panels) */
  compact?: boolean;
}

const ConditionsField = ({
  condition,
  setCondition,
  dataSourceOptions,
  attributeFieldOptions,
  segmentOptions,
  eventOptions = [],
  eventOperators,
  compact = false,
}: ConditionsFieldProps) => {
  const fieldCol = compact ? FIELD_COL_COMPACT : FIELD_COL_NORMAL;
  const labelCls = compact ? LABEL_COMPACT : LABEL_NORMAL;
  const inputCls = `${INPUT_BASE} ${compact ? INPUT_COMPACT : INPUT_NORMAL}`;
  const btnCls = `${BTN_BASE} ${compact ? BTN_COMPACT : BTN_NORMAL}`;
  const rowGap = compact ? ROW_GAP_COMPACT : ROW_GAP_NORMAL;
  const dateRowMin = compact ? DATE_ROW_COMPACT : DATE_ROW_NORMAL;
  const dateBtnMin = compact ? DATE_BTN_COMPACT : DATE_BTN_NORMAL;
  const [err, setErr] = useState<Err>({});
  const [dtOpen, setDtOpen] = useState<{
    idx: number;
    path: string;
    field?: 'start' | 'end';
  } | null>(null);
  const attrs = useSelector(selectAttributes);
  const segs = useSelector(selectSegments);
  const segsLoading = useSelector(selectSegmentsLoading);

  const toOpt = (item: IAttribute | string, t: 'common' | 'dynamic') =>
    typeof item === 'string'
      ? { label: item, value: item, tag: '' }
      : {
          label: t === 'common' ? (item.label ?? '') : (item.name ?? ''),
          value: t === 'common' ? item.name : `attributes.${item.name}`,
          tag: item.tag,
        };
  const common = COMMON_FIELDS.map((i) => toOpt(i, 'common'));
  const commonVals = new Set(common.map((f) => f.value));
  const optionsFromRedux = {
    'Contact Fields': common,
    Attributes: (attrs?.attributes || [])
      .map((i) => toOpt(i, 'dynamic'))
      .filter((a) => !commonVals.has(a.value)),
  };

  const segOptsFromRedux = segsLoading
    ? [{ label: 'Loading segments...', value: '', tag: '' }]
    : (segs || [])
        .filter((s) => s?.name && s?._id)
        .map((s) => ({ label: s.name, value: s._id || '', tag: s.type || '' }));
  const segOpts = segmentOptions ?? segOptsFromRedux;

  const eventOpts = eventOptions;
  const eventSourceOperators = eventOperators ?? DEFAULT_EVENT_OPERATORS;

  const fmtDisplay = (v: ConditionValue): string => {
    const d = parseDt(v);
    if (!d)
      return v?.date
        ? new Date(v.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : 'Select Date & Time';
    if (v?.datetime)
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: v?.timezone || 'UTC',
        timeZoneName: 'short',
        hour12: true,
      }).format(d);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  const toPickerVal = (v: ConditionValue): DateTimePickerValue | null => {
    const d = parseDt(v);
    if (!d) return null;
    return {
      datetime: d,
      timestamp: v?.timestamp ?? Math.floor(d.getTime() / 1000),
      timezone:
        v?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'UTC',
    };
  };
  const fmtLocal = (d: Date) => ({
    date: d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
  });

  const getErr = (
    _i: number,
    f: 'field' | 'operator' | 'value',
    gi?: number
  ) => (gi !== undefined ? undefined : err[f]);
  useEffect(() => {
    setErr(validateCondition(condition).fieldErrors || {});
  }, [condition]);
  const upd = (fn: (c: Condition) => Condition) => setCondition(fn(condition));

  const opType = (ov: string): keyof typeof OP =>
    ov === OP.HAS_PROPERTY
      ? 'HAS_PROPERTY'
      : ov === OP.AT_LEAST_ONE
        ? 'AT_LEAST_ONE'
        : 'DEFAULT';
  const opUpdate = (ov: string): Partial<Condition> => {
    const t = opType(ov);
    if (t === 'HAS_PROPERTY') return { operator: ov, value: DEF.has_property };
    if (t === 'AT_LEAST_ONE')
      return {
        operator: ov,
        value: DEF.at_least_one.value,
        arrayType: DEF.at_least_one.arrayType,
      };
    return { operator: ov, value: null };
  };

  const renderCondition = (
    c: Condition,
    index: number,
    _level = 0,
    parentPath = '',
    parentUpd?: (fn: (x: Condition) => Condition) => void,
    arrayCond?: string,
    groupIdx?: number
  ) => {
    const {
      field,
      operator,
      value,
      condition: srcType,
      arrayType,
      property,
    } = c;
    const disGrp =
      field === 'email'
        ? SEGMENT_OPERATORS_GROUPS.UNIX_TIMESTAMP
        : TIME_FIELDS.has(field || '') ||
            arrayCond ||
            field?.includes('attributes.')
          ? SEGMENT_OPERATORS_GROUPS.EMAIL
          : '';
    const apply = (fn: (x: Condition) => Condition) =>
      parentUpd ? parentUpd(fn) : upd(fn);
    const onOpChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const ov = e.target.value;
      const next = { ...c, ...opUpdate(ov) };
      if (opType(ov) !== 'AT_LEAST_ONE' && 'arrayType' in next)
        delete next.arrayType;
      apply(() => next);
    };

    const v = value as ConditionValue;
    const mergeVal = (prev: unknown, patch: object) =>
      (typeof prev === 'object' && prev !== null
        ? { ...prev, ...patch }
        : patch) as ConditionValue;
    return (
      <span className={`flex flex-wrap w-full items-start ${rowGap}`}>
        {!arrayCond && (
          <div className={fieldCol}>
            <label className={labelCls}>Data Source</label>
            <Select
              options={dataSourceOptions}
              placeholder="Select Condition"
              value={srcType}
              size={compact ? 'sm' : 'md'}
              onChange={(e) =>
                apply(() => ({
                  ...c,
                  condition: e.target.value,
                  field: '',
                  operator: '',
                  value: null,
                }))
              }
            />
          </div>
        )}
        {!arrayCond && (
          <div className={compact ? `${fieldCol} min-w-0` : fieldCol}>
            <label className={labelCls}>
              {srcType === 'event' ? 'Event' : 'Field'}
            </label>
            <SearchSelect
              options={
                srcType === 'segment'
                  ? segOpts
                  : srcType === 'event'
                    ? eventOpts.length
                      ? { Event: eventOpts }
                      : [{ label: 'No events', value: '', tag: '' }]
                    : optionsFromRedux
              }
              placeholder={
                srcType === 'segment'
                  ? 'Select Segment'
                  : srcType === 'event'
                    ? 'Select Event'
                    : 'Select Field'
              }
              searchPlaceholder={
                srcType === 'segment'
                  ? 'Find a segment...'
                  : srcType === 'event'
                    ? 'Find event...'
                    : 'Find or specify an attribute...'
              }
              value={field}
              onChange={(val) => apply((prev) => ({ ...prev, field: val }))}
              size={compact ? 'sm' : 'md'}
              className={getErr(index, 'field', groupIdx) ? ERR_CLS : ''}
            />
            {getErr(index, 'field', groupIdx) && (
              <div
                className={`text-destructive mt-0.5 flex items-center gap-1 ${compact ? 'text-xs' : 'text-sm'}`}
              >
                <span className="w-1 h-1 bg-destructive rounded-full" />
                {getErr(index, 'field', groupIdx)}
              </div>
            )}
          </div>
        )}
        {arrayCond === 'propertyValue' && (
          <div className={fieldCol}>
            <label className={labelCls}>Property Name</label>
            <Input
              placeholder="Enter Property Name"
              value={property || ''}
              onChange={(e) =>
                apply((prev) => ({ ...prev, property: e.target.value }))
              }
              className={inputCls}
            />
          </div>
        )}
        <div className={fieldCol}>
          <label className={labelCls}>Operator</label>
          <Select
            options={
              srcType === 'segment'
                ? SEGMENT_SOURCE_OPERATORS
                : srcType === 'event'
                  ? { Event: eventSourceOperators }
                  : SEGMENT_OPERATORS
            }
            placeholder="Select Operator"
            value={operator}
            size={compact ? 'sm' : 'md'}
            disabledGroup={
              srcType === 'segment' || srcType === 'event' ? undefined : disGrp
            }
            onChange={onOpChange}
            className={getErr(index, 'operator', groupIdx) ? ERR_CLS : ''}
          />
          {getErr(index, 'operator', groupIdx) && (
            <div
              className={`text-destructive mt-0.5 flex items-center gap-1 ${compact ? 'text-xs' : 'text-sm'}`}
            >
              <span className="w-1 h-1 bg-destructive rounded-full" />
              {getErr(index, 'operator', groupIdx)}
            </div>
          )}
        </div>
        {operator === 'at_least_one' && (
          <div className={fieldCol}>
            <label className={labelCls}>Array Type</label>
            <Select
              options={[
                { label: 'Array Value', value: 'arrayValue' },
                { label: 'Property Value', value: 'propertyValue' },
              ]}
              placeholder="Select Field"
              value={arrayType}
              size={compact ? 'sm' : 'md'}
              onChange={(e) =>
                apply((prev) => ({
                  ...prev,
                  arrayType: e.target.value,
                  value: {
                    operator: 'equals',
                    ...(e.target.value === 'propertyValue'
                      ? { property: '', value: null }
                      : { value: null }),
                  },
                }))
              }
            />
          </div>
        )}
        {(arrayType || operator === 'has_property') && (
          <div className={`w-full ${compact ? 'text-xs' : 'text-sm'}`}>
            {renderCondition(
              value as Condition,
              index,
              0,
              parentPath,
              (fn: (x: Condition) => Condition) =>
                apply((prev) => ({
                  ...prev,
                  value: fn(prev.value as Condition),
                })),
              arrayType ?? 'propertyValue'
            )}
          </div>
        )}
        {srcType !== 'segment' &&
        srcType !== 'event' &&
        !NO_VAL_OPS.has(operator) ? (
          <div className={fieldCol}>
            <label className={labelCls}>Value</label>
            <Input
              placeholder="Enter Value"
              value={(value as string) || ''}
              onChange={(e) =>
                apply((prev) => ({ ...prev, value: e.target.value }))
              }
              className={
                getErr(index, 'value', groupIdx)
                  ? `${inputCls} ${ERR_CLS}`
                  : `${inputCls} focus:border-primary focus:ring-primary`
              }
            />
            {getErr(index, 'value', groupIdx) && (
              <div
                className={`text-destructive mt-0.5 flex items-center gap-1 ${compact ? 'text-xs' : 'text-sm'}`}
              >
                <span className="w-1 h-1 bg-destructive rounded-full" />
                {getErr(index, 'value', groupIdx)}
              </div>
            )}
          </div>
        ) : operator === 'is_between' ? (
          <div
            className={`flex-shrink-0 flex flex-col ${compact ? 'text-xs' : 'text-sm'} ${dateRowMin}`}
          >
            <label className={labelCls}>Value</label>
            <div className={`flex items-center ${rowGap}`}>
              <button
                type="button"
                onClick={() =>
                  setDtOpen({ idx: index, path: parentPath, field: 'start' })
                }
                className={`${dateBtnMin} ${btnCls}`}
              >
                {v?.start ? fmtDtStr(v.start) : 'Select Start Date'}
              </button>
              <span
                className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}
              >
                to
              </span>
              <button
                type="button"
                onClick={() =>
                  setDtOpen({ idx: index, path: parentPath, field: 'end' })
                }
                className={`${dateBtnMin} ${btnCls}`}
              >
                {v?.end ? fmtDtStr(v.end) : 'Select End Date'}
              </button>
              {dtOpen?.idx === index &&
                dtOpen?.path === parentPath &&
                (dtOpen?.field === 'start' || dtOpen?.field === 'end') && (
                  <DateTimePicker
                    isOpen
                    value={
                      dtOpen.field === 'start' && v?.start
                        ? toDtPickerVal(v, 'start')
                        : dtOpen.field === 'end' && v?.end
                          ? toDtPickerVal(v, 'end')
                          : null
                    }
                    onChange={(dv: DateTimePickerValue) =>
                      apply((prev) => ({
                        ...prev,
                        value: mergeVal(prev.value, {
                          [dtOpen.field!]: dv.datetime.toISOString(),
                          [dtOpen.field === 'start'
                            ? 'startTimestamp'
                            : 'endTimestamp']: dv.timestamp,
                          timezone: dv.timezone,
                        }),
                      }))
                    }
                    onClose={() => setDtOpen(null)}
                  />
                )}
            </div>
          </div>
        ) : operator === 'between' ? (
          <div
            className={`flex-shrink-0 flex flex-col ${compact ? 'text-xs' : 'text-sm'} ${dateRowMin}`}
          >
            <label className={labelCls}>Value</label>
            <div className={`flex items-center ${rowGap}`}>
              <Input
                placeholder="From"
                value={v?.start || ''}
                onChange={(e) =>
                  apply((prev) => ({
                    ...prev,
                    value: mergeVal(prev.value, { start: e.target.value }),
                  }))
                }
                className={inputCls}
              />
              <span
                className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}
              >
                to
              </span>
              <Input
                placeholder="To"
                value={v?.end || ''}
                onChange={(e) =>
                  apply((prev) => ({
                    ...prev,
                    value: mergeVal(prev.value, { end: e.target.value }),
                  }))
                }
                className={inputCls}
              />
            </div>
          </div>
        ) : (
          srcType !== 'segment' &&
          srcType !== 'event' &&
          !DATE_ONLY_OPS.has(operator) && (
            <div
              className={`flex flex-col w-full ${compact ? 'gap-1.5 text-xs' : 'gap-2 text-sm'}`}
            >
              <div className={`flex flex-wrap w-full ${rowGap}`}>
                <div className={fieldCol}>
                  <label className={labelCls}>Value type</label>
                  <Select
                    options={[
                      { label: 'Specific Value', value: 'specific_value' },
                      { label: 'Relative Date', value: 'relative_date' },
                    ]}
                    placeholder="Select Value Type"
                    value={v?.dateType || ''}
                    size={compact ? 'sm' : 'md'}
                    onChange={(e) =>
                      apply((prev) => ({
                        ...prev,
                        value:
                          e.target.value === 'relative_date'
                            ? {
                                dateType: 'relative_date',
                                days: '',
                                timeDirection: '',
                              }
                            : e.target.value === 'specific_value'
                              ? { dateType: 'specific_value', date: '' }
                              : { dateType: '' },
                      }))
                    }
                  />
                </div>
                {v?.dateType === 'relative_date' && (
                  <div
                    className={`flex items-center flex-wrap items-end ${rowGap} ${compact ? 'text-xs' : 'text-sm'}`}
                  >
                    <span
                      className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}
                    >
                      of
                    </span>
                    <div className="flex flex-col">
                      <label className={labelCls}>Days</label>
                      <Input
                        type="number"
                        className={inputCls}
                        placeholder="Number"
                        value={v?.days || ''}
                        onChange={(e) =>
                          apply((prev) => ({
                            ...prev,
                            value: mergeVal(prev.value, {
                              days: e.target.value,
                            }),
                          }))
                        }
                      />
                    </div>
                    <span
                      className={`text-gray-500 px-1 ${compact ? 'text-xs' : 'text-sm'}`}
                    >
                      days
                    </span>
                    <div
                      className={`flex flex-col ${compact ? 'min-w-[80px]' : 'min-w-[100px]'}`}
                    >
                      <label className={labelCls}>When</label>
                      <Select
                        options={[
                          { label: 'ago', value: 'ago' },
                          { label: 'from now', value: 'from_now' },
                        ]}
                        placeholder="When"
                        value={v?.timeDirection || ''}
                        onChange={(e) =>
                          apply((prev) => ({
                            ...prev,
                            value: mergeVal(prev.value, {
                              timeDirection: e.target.value,
                            }),
                          }))
                        }
                      />
                    </div>
                    {v?.days &&
                      v?.timeDirection &&
                      (() => {
                        const n = parseInt(v.days, 10);
                        if (isNaN(n)) return null;
                        const d = new Date();
                        d.setDate(
                          d.getDate() +
                            (v.timeDirection === 'from_now' ? n : -n)
                        );
                        const { date: fd, time: ft } = fmtLocal(d);
                        return (
                          <div className="relative inline-block group">
                            <span
                              className={`text-gray-600 ml-2 cursor-help underline decoration-dotted ${compact ? 'text-xs' : 'text-sm'}`}
                            >
                              Expected Output
                            </span>
                            <div
                              className={`${TOOLTIP} ${compact ? 'mt-1' : 'mt-2'}`}
                            >
                              <div className={TOOLTIP_BOX}>
                                <div className="font-semibold mb-1 text-white">
                                  Date & Time
                                </div>
                                <div className="text-gray-200">
                                  {fd} {ft}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                )}
                {v?.dateType === 'specific_value' && (
                  <div
                    className={`flex flex-col ${compact ? 'gap-0.5' : 'gap-1'}`}
                  >
                    <label
                      className={`${labelCls} ${compact ? 'ml-2' : 'ml-6'}`}
                    >
                      Date & time
                    </label>
                    <div
                      className={`flex items-center flex-wrap ${rowGap} ${compact ? 'text-xs' : 'text-sm'}`}
                    >
                      <span className="text-gray-500">of</span>
                      <button
                        type="button"
                        aria-label="Select date and time"
                        onClick={() =>
                          setDtOpen({ idx: index, path: parentPath })
                        }
                        className={`${compact ? 'min-w-[140px]' : 'min-w-[180px]'} ${btnCls}`}
                      >
                        {fmtDisplay(v)}
                      </button>
                      {parseDt(v) &&
                        (() => {
                          const { date: fd, time: ft } = fmtLocal(parseDt(v)!);
                          return (
                            <div className="relative inline-block group">
                              <span
                                className={`text-gray-600 ml-2 cursor-help underline decoration-dotted ${compact ? 'text-xs' : 'text-sm'}`}
                              >
                                Local Timezone
                              </span>
                              <div
                                className={`${TOOLTIP} ${compact ? 'mt-1' : 'mt-2'}`}
                              >
                                <div className={TOOLTIP_BOX}>
                                  <div className="font-semibold mb-1 text-white">
                                    Date & Time
                                  </div>
                                  <div className="text-gray-200">
                                    {fd} {ft}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      {dtOpen?.idx === index &&
                        dtOpen?.path === parentPath &&
                        !dtOpen?.field && (
                          <DateTimePicker
                            isOpen
                            value={toPickerVal(v)}
                            onChange={(dv: DateTimePickerValue) => {
                              const iso = dv.datetime.toISOString();
                              apply((prev) => ({
                                ...prev,
                                value: mergeVal(prev.value, {
                                  datetime: iso,
                                  timestamp: dv.timestamp,
                                  timezone: dv.timezone,
                                  date: iso.split('T')[0],
                                }),
                              }));
                            }}
                            onClose={() => setDtOpen(null)}
                          />
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </span>
    );
  };

  return (
    <div
      className={`flex flex-col w-full ${compact ? ROOT_GAP_COMPACT : ROOT_GAP_NORMAL}`}
    >
      {renderCondition(condition, 0, 0)}
    </div>
  );
};

export default ConditionsField;
